import { type FC, useState } from 'react';
import LikeHeartIcon from 'dibs-icons/exports/legacy/LikeHeartIcon';
import { type TooltipDirection } from 'dibs-elements/exports/Tooltip';
import { Link } from 'dibs-elements/exports/Link';
import classNames from 'classnames';

import PhotoLikeFeedbackTooltip from './components/PhotoLikeFeedbackTooltip';
import PhotoLikeFeedbackToast from './components/PhotoLikeFeedbackToast';
import ContentWrapper from './components/ContentWrapper';
import { useHandlePhotoLike } from './useHandlePhotoLike';
import { type PhotoLikeProps } from './types';
import { formatLikes } from './helpers/formatLikes';

import styles from './PhotoHeart.scss';

type Props = {
    tooltipDirection?: TooltipDirection;
    isMobile?: boolean;
    totalPhotoLikes?: number | null;
} & PhotoLikeProps;

const PhotoHeart: FC<Props> = ({
    photoId,
    photoLikeId,
    photoNodeId,
    onPhotoLikeAdded,
    tooltipDirection,
    isMobile,
    totalPhotoLikes,
}) => {
    const formattedTotalLikes = formatLikes(totalPhotoLikes || 0);
    const showTotalLikes = !!formattedTotalLikes;

    const [isFeedbackVisible, setIsFeedbackVisible] = useState(false);
    const { handlePhotoLike, isAnimated, isLiked } = useHandlePhotoLike({
        photoId,
        photoLikeId,
        photoNodeId,
        onPhotoLikeAdded,
        showFeedback: () => setIsFeedbackVisible(true),
    });

    return (
        <div className={styles.photoHeartWrapper}>
            <ContentWrapper fullOpacity={isLiked}>
                <Link
                    underline="none"
                    className={classNames(styles.iconWrapper, {
                        [styles.withTotalLikes]: showTotalLikes,
                    })}
                    onClick={handlePhotoLike}
                    dataTn={isLiked ? 'photo-saved-icon' : 'photo-save-icon'}
                >
                    <LikeHeartIcon
                        className={styles.heartIcon}
                        animateHeartBeat={isAnimated}
                        isFilled={isLiked}
                        theme="dark"
                    />
                    {showTotalLikes && (
                        <span className={styles.photoLikesCountWrapper}>{formattedTotalLikes}</span>
                    )}
                </Link>
            </ContentWrapper>
            {isMobile ? (
                <PhotoLikeFeedbackToast
                    isToastVisible={isFeedbackVisible}
                    onClose={() => setIsFeedbackVisible(false)}
                />
            ) : (
                <PhotoLikeFeedbackTooltip
                    onClose={() => setIsFeedbackVisible(false)}
                    isTooltipVisible={isFeedbackVisible}
                    tooltipDirection={tooltipDirection}
                />
            )}
        </div>
    );
};

export default PhotoHeart;
